/**
 * Data Entry Page - Patient-First Workflow
 * 1. Select a patient from the list
 * 2. See form checklist grouped by category
 * 3. Click a form to edit it inline
 * 4. After submit, stay on patient to enter more forms
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { studiesApi, subjectsApi, formsApi, dataApi, DataEntry as DataEntryType, Subject } from '../services/api';
import { FormRenderer } from '../components/FormRenderer';
import FileManager from '../components/FileManager/FileManager';
import { FormState } from '../types/form';
import {
  FileText,
  Search,
  Plus,
  CheckCircle,
  Edit3,
  PenTool,
  AlertTriangle,
  Shield,
  ArrowLeft,
  User,
  ChevronRight,
  ChevronDown,
  Circle,
  Upload,
  Paperclip,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Types for subject status endpoint response
interface FormStatusEntry {
  entryNumber: number;
  entryId: string;
  recordId: string;
  status: string;
  completedFields: number;
  totalFields: number;
  missingRequired: string[];
  missingOptional?: string[];
  isComplete: boolean;
  lastSavedAt: string;
}

interface FormStatus {
  formId: string;
  formName: string;
  formType: string;
  category?: string;
  repeating: boolean;
  status: 'missing' | 'incomplete' | 'complete';
  entries: FormStatusEntry[];
  totalFields: number;
  requiredFields: number;
  missingRequired?: string[];
}

const DataEntry: React.FC = () => {
  const { user, studyCode } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [activeVisitId, setActiveVisitId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSignModal, setShowSignModal] = useState(false);
  const [signPassword, setSignPassword] = useState('');
  const [signMeaning, setSignMeaning] = useState('I have reviewed this data and confirm it is accurate and complete.');
  const [signError, setSignError] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showFilesPanel, setShowFilesPanel] = useState(false);

  // Fetch studies (auto-select single study)
  const { data: studiesData } = useQuery({
    queryKey: ['studies'],
    queryFn: studiesApi.list,
  });
  const studies = studiesData?.studies || [];
  // Scope to the logged-in study (multi-study safe); fall back to single-study auto-select
  const selectedStudy = studyCode || (studies.length === 1 ? studies[0].studyCode : '');

  // Fetch subjects — always refetch on mount so a newly-created subject shows
  // without a manual refresh (global staleTime is 5 min)
  const { data: subjectsData } = useQuery({
    queryKey: ['subjects', selectedStudy],
    queryFn: () => subjectsApi.list(selectedStudy),
    enabled: !!selectedStudy,
    refetchOnMount: 'always',
  });
  const subjects = subjectsData?.subjects || [];

  // Auto-select subject from URL param (e.g. /data?subject=<_id>&section=cec)
  const subjectParam = searchParams.get('subject');
  const sectionParam = searchParams.get('section');
  useEffect(() => {
    if (subjectParam && subjects.length > 0 && !selectedSubject) {
      const found = subjects.find(s => s._id === subjectParam);
      if (found) {
        setSelectedSubject(found);
        // Auto-expand the requested section (e.g. 'cec')
        if (sectionParam) {
          setExpandedCategories(new Set([sectionParam]));
        }
        // Clear the params so back button works cleanly
        setSearchParams({}, { replace: true });
      }
    }
  }, [subjectParam, sectionParam, subjects, selectedSubject, setSearchParams]);

  // Fetch forms list (for getting _id from formId)
  const { data: formsData } = useQuery({
    queryKey: ['forms', selectedStudy],
    queryFn: () => formsApi.list(selectedStudy),
    enabled: !!selectedStudy,
  });
  const forms = formsData?.forms || [];

  // Fetch subject status (form checklist)
  const { data: statusData } = useQuery({
    queryKey: ['subjectStatus', selectedSubject?._id],
    queryFn: () => dataApi.subjectStatus(selectedSubject!._id),
    enabled: !!selectedSubject?._id,
  });
  const visitGroups = statusData?.visits || [];
  const eventDrivenForms: FormStatus[] = statusData?.eventDriven || [];
  const coreLabForms: FormStatus[] = statusData?.coreLab || [];
  const cecForms: FormStatus[] = (statusData as any)?.cec || [];

  // Fetch the active entry data
  const { data: entryData } = useQuery({
    queryKey: ['data', activeEntryId],
    queryFn: () => dataApi.get(activeEntryId!),
    enabled: !!activeEntryId,
  });

  // Fetch the form template for active form
  const activeFormTemplate = forms.find(f => f.formId === activeFormId);
  const { data: formTemplateData } = useQuery({
    queryKey: ['form', activeFormTemplate?._id],
    queryFn: () => formsApi.get(activeFormTemplate!._id),
    enabled: !!activeFormTemplate?._id,
  });

  // Create entry mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<DataEntryType>) => dataApi.create(data),
    onSuccess: (response) => {
      setActiveEntryId(response.entry._id);
      queryClient.invalidateQueries({ queryKey: ['subjectStatus', selectedSubject?._id] });
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DataEntryType> }) =>
      dataApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data', activeEntryId] });
      queryClient.invalidateQueries({ queryKey: ['subjectStatus', selectedSubject?._id] });
      queryClient.invalidateQueries({ queryKey: ['subject-summary'] });
      // Close form after save
      setActiveFormId(null);
      setActiveEntryId(null);
      setActiveVisitId(null);
    },
  });

  // Sign mutation
  const signMutation = useMutation({
    mutationFn: ({ id, password, meaning }: { id: string; password: string; meaning: string }) =>
      dataApi.sign(id, password, meaning),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data'] });
      queryClient.invalidateQueries({ queryKey: ['subjectStatus', selectedSubject?._id] });
      setShowSignModal(false);
      setSignPassword('');
      setSignError('');
    },
    onError: (error: Error) => {
      setSignError(error.message || 'Signature failed. Please check your password.');
    },
  });

  // Handle selecting a form from the checklist
  const handleFormClick = useCallback(async (formStatus: FormStatus, entryId?: string, visitId?: string) => {
    setShowFilesPanel(false);
    setActiveFormId(formStatus.formId);
    setActiveVisitId(visitId || null);

    if (entryId) {
      // Existing entry - load it
      setActiveEntryId(entryId);
    } else if (formStatus.status === 'missing' && !formStatus.repeating) {
      // Missing non-repeating form - auto-create entry
      createMutation.mutate({
        studyCode: selectedStudy,
        subjectId: selectedSubject!.subjectId,
        subjectObjectId: selectedSubject!._id,
        formId: formStatus.formId,
        visitId: visitId,
        data: [],
      } as any);
    }
  }, [selectedStudy, selectedSubject, createMutation]);

  // Handle adding new repeating form entry
  const handleAddRepeating = useCallback((formId: string, visitId?: string) => {
    setActiveFormId(formId);
    setActiveVisitId(visitId || null);
    createMutation.mutate({
      studyCode: selectedStudy,
      subjectId: selectedSubject!.subjectId,
      subjectObjectId: selectedSubject!._id,
      formId: formId,
      visitId: visitId,
      data: [],
    } as any);
  }, [selectedStudy, selectedSubject, createMutation]);

  // Handle save - backend expects data as { fieldId: value } object
  const handleSave = async (formData: FormState) => {
    if (!activeEntryId) return;
    try {
      await saveMutation.mutateAsync({
        id: activeEntryId,
        data: { data: formData } as any,
      });
    } catch (err) {
      console.error('Save error:', err);
    }
    // Always close form and refresh checklist
    queryClient.invalidateQueries({ queryKey: ['subjectStatus', selectedSubject?._id] });
    setActiveFormId(null);
    setActiveEntryId(null);
    setActiveVisitId(null);
  };

  // Handle sign
  const handleSign = () => {
    if (!activeEntryId || !signPassword) return;
    signMutation.mutate({
      id: activeEntryId,
      password: signPassword,
      meaning: signMeaning,
    });
  };

  // Change patient
  const handleChangePatient = () => {
    setSelectedSubject(null);
    setActiveFormId(null);
    setActiveEntryId(null);
    setActiveVisitId(null);
  };

  // Filter subjects by search (matches displayId like "012-002", subjectId, or site name)
  const filteredSubjects = subjects.filter(s => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const siteName = typeof s.site === 'object' && s.site ? (s.site as any).siteName || '' : '';
    return (s.displayId && s.displayId.toLowerCase().includes(term)) ||
      s.subjectId.toLowerCase().includes(term) ||
      siteName.toLowerCase().includes(term);
  });

  // Toggle accordion section
  const toggleCategory = (key: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Auto-expand section when a form is active
  useEffect(() => {
    if (activeVisitId) {
      setExpandedCategories(prev => new Set(prev).add(activeVisitId));
    } else if (activeFormId) {
      // Check event-driven and core lab
      if (eventDrivenForms.some(f => f.formId === activeFormId)) {
        setExpandedCategories(prev => new Set(prev).add('event-driven'));
      } else if (coreLabForms.some(f => f.formId === activeFormId)) {
        setExpandedCategories(prev => new Set(prev).add('core-lab'));
      } else if (cecForms.some(f => f.formId === activeFormId)) {
        setExpandedCategories(prev => new Set(prev).add('cec'));
      }
    }
  }, [activeFormId, activeVisitId, eventDrivenForms, coreLabForms, cecForms]);

  // Section completion summary
  const getSectionSummary = (forms: FormStatus[]) => {
    const complete = forms.filter(f => f.status === 'complete').length;
    const total = forms.length;
    return { complete, total, allComplete: complete === total && total > 0 };
  };

  // Build accordion sections by role:
  // site-rc / site: visits + event-driven (writable)
  // analyst: Core Lab only (writable)
  // cec: all site data (read-only) + CEC Adjudication (writable)
  // admin: everything (writable)
  const isSiteRole = user?.role === 'site-rc' || user?.role === 'site';
  const isAdmin = user?.role === 'admin';
  const isAnalyst = user?.role === 'analyst';
  const isCec = user?.role === 'cec';

  const accordionSections: { key: string; label: string; forms: FormStatus[]; visitId?: string; readOnly?: boolean }[] = [
    // Visit-based forms — site roles + admin + cec (read-only for cec)
    ...(isSiteRole || isAdmin || isCec ? visitGroups.map((v: any) => ({
      key: v.visitId,
      label: v.visitName,
      forms: v.forms as FormStatus[],
      visitId: v.visitId as string,
      readOnly: isCec,
    })) : []),
    // Event-driven forms — site roles + admin + cec (read-only for cec)
    ...(eventDrivenForms.length > 0 && (isSiteRole || isAdmin || isCec) ? [{
      key: 'event-driven',
      label: 'Event-Driven',
      forms: eventDrivenForms,
      readOnly: isCec,
    }] : []),
    // Core Lab — admin + analyst (writable), cec (read-only)
    ...(coreLabForms.length > 0 && (isAdmin || isAnalyst || isCec) ? [{
      key: 'core-lab',
      label: 'Core Lab',
      forms: coreLabForms,
      readOnly: isCec,
    }] : []),
    // CEC Adjudication — admin + cec only
    ...(cecForms.length > 0 && (isAdmin || isCec) ? [{
      key: 'cec',
      label: 'CEC Adjudication',
      forms: cecForms,
    }] : []),
  ];

  // Status display helpers
  const getFormStatusIcon = (status: string) => {
    switch (status) {
      case 'complete': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'incomplete': return <Edit3 className="w-4 h-4 text-amber-500" />;
      case 'missing': return <Circle className="w-4 h-4 text-gray-300" />;
      default: return <Circle className="w-4 h-4 text-gray-300" />;
    }
  };

  const getEntryStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      new: 'bg-gray-100 text-gray-600',
      'in-progress': 'bg-amber-100 text-amber-700',
      complete: 'bg-green-100 text-green-700',
      submitted: 'bg-blue-100 text-blue-700',
      signed: 'bg-emerald-100 text-emerald-700',
      locked: 'bg-gray-100 text-gray-600',
    };
    const labels: Record<string, string> = {
      new: 'New',
      'in-progress': 'Incomplete',
      complete: 'Complete',
      submitted: 'Submitted',
      signed: 'Signed',
      locked: 'Locked',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[status] || styles.new}`}>
        {labels[status] || status}
      </span>
    );
  };

  const currentEntry = entryData?.entry || null;
  const currentTemplate = formTemplateData?.form || null;
  // Determine if current form's section is read-only for this user
  const activeSection = accordionSections.find(s =>
    s.forms.some(f => f.formId === activeFormId)
  );
  const isSectionReadOnly = activeSection?.readOnly || false;
  const isFormDisabled = currentEntry?.isLocked || currentEntry?.status === 'signed' || isSectionReadOnly;

  // ─── PATIENT SELECTION MODE ───
  if (!selectedSubject) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Entry</h1>
          <p className="text-gray-500">Select a patient to enter data</p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opci-navy-300 focus:border-transparent"
            />
          </div>
        </div>

        {/* Patient List */}
        <div className="bg-white rounded-xl shadow-sm">
          {filteredSubjects.length === 0 ? (
            <div className="text-center py-12">
              <User className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-gray-500">
                {subjects.length === 0 ? 'No patients yet. Create a subject first.' : 'No matching patients.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredSubjects.map((subject) => (
                <button
                  key={subject._id}
                  onClick={() => setSelectedSubject(subject)}
                  className="w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-opci-navy-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-opci-navy-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{subject.displayId || subject.subjectId}</p>
                      <p className="text-sm text-gray-500">
                        {typeof subject.site === 'object' && subject.site
                          ? (subject.site as { siteName: string }).siteName
                          : ''}
                        {subject.status && (
                          <span className="ml-2 capitalize">{subject.status}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── DATA ENTRY MODE ───
  return (
    <div className="space-y-4">
      {/* Header with patient info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/subjects')}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Patient: {selectedSubject.displayId || selectedSubject.subjectId}
            </h1>
            <p className="text-gray-500 text-sm">
              {typeof selectedSubject.site === 'object' && selectedSubject.site
                ? (selectedSubject.site as { siteName: string }).siteName
                : ''}
              {selectedSubject.status && (
                <span className="ml-2 capitalize">({selectedSubject.status})</span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleChangePatient}
          className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Change Patient
        </button>
      </div>

      {/* Summary bar */}
      {statusData?.summary && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{statusData.summary.completeForms}</p>
            <p className="text-xs text-green-600">Complete</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{statusData.summary.incompleteForms}</p>
            <p className="text-xs text-amber-600">In Progress</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-700">{statusData.summary.missingForms}</p>
            <p className="text-xs text-gray-600">Not Started</p>
          </div>
        </div>
      )}

      {/* Two-panel layout: form list + editor */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Form Checklist */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <h2 className="font-semibold text-gray-800">Forms</h2>
          </div>
          <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
            {accordionSections.map((section) => {
              const isExpanded = expandedCategories.has(section.key);
              const summary = getSectionSummary(section.forms);
              return (
              <div key={section.key}>
                <button
                  onClick={() => toggleCategory(section.key)}
                  className="w-full px-4 py-3 bg-gray-50 border-y border-gray-100 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded
                      ? <ChevronDown className="w-4 h-4 text-gray-400" />
                      : <ChevronRight className="w-4 h-4 text-gray-400" />
                    }
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {section.label}
                    </span>
                    {section.readOnly && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 uppercase">View Only</span>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    summary.allComplete ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {summary.complete}/{summary.total}
                  </span>
                </button>
                {isExpanded && (
                <div className="divide-y divide-gray-50">
                  {section.forms.map((fs) => (
                    <div key={`${section.key}-${fs.formId}`}>
                      {!fs.repeating ? (
                        <button
                          onClick={() => {
                            const entryId = fs.entries.length > 0 ? fs.entries[0].entryId : undefined;
                            handleFormClick(fs, entryId, section.visitId);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                            activeFormId === fs.formId && activeVisitId === (section.visitId || null) ? 'bg-opci-navy-50 border-l-4 border-opci-navy-400' : ''
                          }`}
                        >
                          {getFormStatusIcon(fs.status)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {fs.formName}
                            </p>
                          </div>
                          {fs.entries.length > 0 && getEntryStatusBadge(fs.entries[0].isComplete ? 'complete' : fs.entries[0].status)}
                        </button>
                      ) : (
                        <div>
                          <div className="px-4 py-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getFormStatusIcon(fs.status)}
                              <span className="text-sm font-medium text-gray-900">{fs.formName}</span>
                              <span className="text-xs text-gray-400">({fs.entries.length})</span>
                            </div>
                            {!section.readOnly && (
                              <button
                                onClick={() => handleAddRepeating(fs.formId, section.visitId)}
                                className="text-opci-navy-500 hover:text-opci-navy-600 p-1"
                                title="Add new entry"
                              >
                                <Plus size={16} />
                              </button>
                            )}
                          </div>
                          {fs.entries.map((entry) => (
                            <button
                              key={entry.entryId}
                              onClick={() => handleFormClick(fs, entry.entryId, section.visitId)}
                              className={`w-full pl-10 pr-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                activeEntryId === entry.entryId ? 'bg-opci-navy-50 border-l-4 border-opci-navy-400' : ''
                              }`}
                            >
                              <span className="text-sm text-gray-700 font-medium">#{entry.entryNumber}</span>
                              <span className="flex-1" />
                              {getEntryStatusBadge(entry.isComplete ? 'complete' : entry.status)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                )}
              </div>
            );
            })}

            {/* Images & Files section */}
            {selectedSubject && (
              <div>
                <button
                  onClick={() => {
                    setShowFilesPanel(!showFilesPanel);
                    if (!showFilesPanel) {
                      setActiveFormId(null);
                      setActiveEntryId(null);
                    }
                  }}
                  className={`w-full px-4 py-3 bg-gray-50 border-y border-gray-100 flex items-center justify-between hover:bg-gray-100 transition-colors ${
                    showFilesPanel ? 'bg-opci-navy-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Images & Files
                    </span>
                  </div>
                  <Upload className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            )}

            {accordionSections.length === 0 && (
              <div className="text-center py-8">
                <FileText className="mx-auto h-10 w-10 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">Loading forms...</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Form Editor or File Manager */}
        <div className="lg:col-span-2">
          {showFilesPanel && selectedSubject ? (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Paperclip className="w-5 h-5 text-gray-600" />
                Images & Files — {selectedSubject.displayId || selectedSubject.subjectId}
              </h2>
              <FileManager
                subjectId={selectedSubject._id}
                studyCode={selectedStudy}
                readOnly={user?.role === 'analyst' || user?.role === 'cec'}
              />
            </div>
          ) : currentTemplate && currentEntry ? (
            <div className="space-y-4">
              <FormRenderer
                template={currentTemplate as any}
                data={currentEntry as any}
                onSave={handleSave}
                disabled={isFormDisabled}
                mode={isFormDisabled ? 'view' : 'edit'}
                visitId={activeVisitId || undefined}
                visitName={activeVisitId ? visitGroups.find((v: any) => v.visitId === activeVisitId)?.visitName : undefined}
                subjectId={selectedSubject?._id}
                dataEntryId={currentEntry?._id}
              />

              {/* E-Signature prompt for submitted forms */}
              {currentEntry.status === 'submitted' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <PenTool className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-800">Electronic Signature Required</h3>
                      <p className="text-amber-700 text-sm mt-1">
                        This form has been submitted and is ready for your electronic signature.
                        Per FDA 21 CFR Part 11, your signature confirms the accuracy of the data.
                      </p>
                      <button
                        onClick={() => setShowSignModal(true)}
                        className="mt-4 flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                      >
                        <PenTool size={18} />
                        Sign Form
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Signed form badge */}
              {currentEntry.status === 'signed' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <Shield className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-green-800">Electronically Signed</h3>
                      <p className="text-green-700 text-sm mt-1">
                        This form has been electronically signed per FDA 21 CFR Part 11 requirements.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : activeFormId && createMutation.isPending ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-opci-navy-500 mx-auto"></div>
              <p className="mt-4 text-gray-500">Creating form entry...</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <FileText className="mx-auto h-16 w-16 text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Select a Form</h3>
              <p className="mt-2 text-gray-500">
                Choose a form from the list to view or edit
              </p>
            </div>
          )}
        </div>
      </div>

      {/* E-Signature Modal */}
      {showSignModal && currentEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <PenTool className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Electronic Signature</h2>
                  <p className="text-sm text-gray-500">FDA 21 CFR Part 11 Compliant</p>
                </div>
              </div>
              <button
                onClick={() => { setShowSignModal(false); setSignPassword(''); setSignError(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Important Notice</p>
                    <p className="mt-1">
                      By signing this form, you are providing a legally binding electronic signature.
                      This action will be recorded in the audit trail.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Document Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Subject:</span>{' '}
                    <span className="font-medium">{selectedSubject.displayId || selectedSubject.subjectId}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Form:</span>{' '}
                    <span className="font-medium">{activeFormId}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signature Meaning *</label>
                <textarea
                  value={signMeaning}
                  onChange={(e) => setSignMeaning(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opci-navy-300 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enter Your Password to Sign *</label>
                <input
                  type="password"
                  value={signPassword}
                  onChange={(e) => { setSignPassword(e.target.value); setSignError(''); }}
                  placeholder="Your account password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opci-navy-300 focus:border-transparent"
                />
              </div>

              {signError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{signError}</p>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Signer Information</h4>
                <div className="text-sm space-y-1">
                  <div><span className="text-gray-500">Name:</span> <span className="font-medium">{user?.fullName}</span></div>
                  <div><span className="text-gray-500">Username:</span> <span className="font-medium">{user?.username}</span></div>
                  <div><span className="text-gray-500">Date/Time:</span> <span className="font-medium">{new Date().toLocaleString()}</span></div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => { setShowSignModal(false); setSignPassword(''); setSignError(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSign}
                  disabled={signMutation.isPending || !signPassword || !signMeaning}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {signMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Signing...
                    </>
                  ) : (
                    <>
                      <PenTool size={18} />
                      Apply Signature
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataEntry;
